import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { getUserProfile, setUser, setToken } from '../redux/auth/authSlice';
import tokenManager from '../utils/tokenManager';
import { logger } from '../utils/logger';
import { saveReturnTo } from '../utils/returnTo';
import { shouldRedirectToOrganizationSetup } from '../utils/authDestination';
import { TaskyHeaderActions } from '../components/tasky/layout/TaskyHeaderActions';
import { useHasInlineHeaderActions } from '../components/tasky/layout/HeaderActionsContext';
import { useIsEmbeddedShell } from '../components/tasky/layout/EmbeddedShellContext';
import TrialBanner from '../components/trial/TrialBanner';
import TrialExpiredProvider from '../components/paywall/TrialExpiredProvider';

// App.tsx から移設（ロジックは変更していない）。テストから単体で描画できるようにするため。

// 広告自動化マン（右側常駐AIアシスタント）。初期バンドルを膨らませないよう遅延読み込みする。
const AdmanWidget = React.lazy(() => import('../components/adman/AdmanWidget'));

// 固定ピル（ヘルプ・クレジット残高）を App 側から重ねると、ページ右上のツールバーに
// 被って 1280 幅では期間セレクタが半分隠れる。どのページが自前のピルを持つかは
// パスの手書きリストで管理していたが、新しいページで登録漏れ＝被りが再発するため、
// ピル側の自己登録（HeaderActionsContext）に置き換えた。ページ内にピルが「実際に
// 表示されている」ときだけ、このオーバーレイを出さない。
const FixedHeaderActions: React.FC = () => {
  const hasInlineHeaderActions = useHasInlineHeaderActions();

  if (hasInlineHeaderActions) return null;

  return (
    /* lg未満は ResponsiveAppShell がトップバーにピルを持つ（登録されるのでここは出ない）。
       ここに来るのはシェルを使わない画面だけで、モバイルtopbar(56px)と重ならないよう下げる */
    <div className="fixed right-4 top-[64px] z-[1000] lg:top-3">
      <TaskyHeaderActions overlay />
    </div>
  );
};

// 認証が必要なルートを保護するためのコンポーネント
const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
  const { token, isAuthenticated, user, isLoading } = useSelector((state: RootState) => state.auth);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  // 新UI（/test-ui）の枠の中の旧画面。トライアルの帯・浮きピル・チャットウィジェットは外側の PrivateRoute が出している
  const embedded = useIsEmbeddedShell();

  useEffect(() => {
    // 初回マウント時のみ認証チェックを実行
    let mounted = true;

    // ページロード時に認証状態を確認
    const checkAuthentication = async () => {
      if (!mounted) return;
      try {
        // トークンマネージャーを使用してトークンを取得
        let accessToken = tokenManager.getToken();
        let currentRefreshToken = tokenManager.getRefreshToken();

        logger.log('Auth check: tokens from tokenManager', {
          hasAccessToken: !!accessToken,
          hasRefreshToken: !!currentRefreshToken
        });

        // トークンがある場合はReduxストアに設定
        if (accessToken) {
          logger.log('Setting token to Redux store from tokenManager');
          dispatch(setToken(accessToken));
        }

        // ユーザー情報もtokenManagerから取得してReduxストアに設定
        const userData = tokenManager.getUserData();
        if (userData) {
          logger.log('Setting user data to Redux store from tokenManager');
          dispatch(setUser(userData));
        }

        // リフレッシュトークンがあるがアクセストークンがない場合はリフレッシュを試みる
        if (currentRefreshToken && !accessToken) {
          logger.log('Auth check: Has refresh token but no access token - attempting refresh');
          try {
            const authService = (await import('../services/api/authService')).default;
            const refreshResult = await authService.refreshToken();
            if (refreshResult && refreshResult.token) {
              logger.log('Auth check: Successfully refreshed token');
              accessToken = refreshResult.token; // 新しいアクセストークンで更新

              // ユーザーデータも保存
              if (refreshResult.user) {
                tokenManager.saveUserData(refreshResult.user);
              }
            }
          } catch (refreshError) {
            console.error('Auth check: Failed to refresh token:', refreshError);
          }
        }

        // アクセストークンがある場合
        if (accessToken) {
          logger.log('Auth check: Access token exists, refresh token:', !!currentRefreshToken);

          // トークンがあるが、ユーザー情報がない場合は取得を試みる
          if (!user) {
            logger.log('Auth check: User info not available, fetching profile');
            await dispatch(getUserProfile()).unwrap();
          }
        }
      } catch (error) {
        // ユーザープロファイル取得に失敗した場合
        console.error('Authentication check failed:', error);

        // この時点でのリフレッシュトークンを再確認
        const currentRefreshToken = tokenManager.getRefreshToken();
        logger.log('Auth check after error: refresh token exists:', !!currentRefreshToken);

        if (!currentRefreshToken) {
          // リフレッシュトークンがない場合のみトークンを削除
          logger.log('No refresh token found, removing access token');
          tokenManager.clearTokens(false); // アクセストークンのみクリア
        } else {
          logger.log('Refresh token exists, maintaining auth state despite profile fetch failure');

          // リフレッシュトークンでトークンの再取得を試みる
          try {
            const authService = (await import('../services/api/authService')).default;
            await authService.refreshToken();
            logger.log('Auth check: Attempted token refresh after profile fetch failure');
          } catch (refreshError) {
            console.error('Auth check: Failed to refresh token after profile fetch failure:', refreshError);
          }
        }
      } finally {
        if (mounted) {
          setIsCheckingAuth(false);
        }
      }
    };

    // 認証チェックを実行
    checkAuthentication();

    // 安全策として最大10秒後には強制的にローディング状態を解除
    const timeout = setTimeout(() => {
      if (mounted) {
        console.warn('Auth check timeout reached - forcing state update');
        setIsCheckingAuth(false);
      }

      // このタイミングでのリフレッシュトークンを再確認
      const currentRefreshToken = tokenManager.getRefreshToken();
      logger.log('Auth check timeout: refresh token exists:', !!currentRefreshToken);

      // タイムアウト時の処理を削除（不要な認証情報削除を防ぐ）
      // 認証チェックはcheckAuthentication関数内で完結させる
    }, 10000); // 10秒

    // クリーンアップ関数（state 経由だと初回描画時の null を掴むため、ローカル変数で解除する）
    return () => {
      mounted = false;
      clearTimeout(timeout);
    };
  }, []); // 依存配列を空にして初回のみ実行

  // 認証チェック中またはロード中は何も表示しない
  if (isCheckingAuth || isLoading) {
    // ローディング中のログを出力
    logger.log('PrivateRoute: Still loading/checking auth', { isCheckingAuth, isLoading });
    return <div>Loading...</div>;
  }

  // 組織未所属ユーザーは組織前提の API が 404/403 を返し、401 ではないので
  // interceptor も動かず空の画面が残る（本番で観測）。会社情報入力へ寄せる。
  // user が null の過渡状態では判定しない（判定条件は authDestination.ts 参照）。
  if (shouldRedirectToOrganizationSetup(user, location.pathname)) {
    logger.log('PrivateRoute: 組織未所属のため /signup/setup へ誘導');
    return <Navigate to="/signup/setup" replace />;
  }

  // ユーザー認証状態の詳細なログ出力
  const refreshToken = tokenManager.getRefreshToken(); // 判定ロジックの直前で取得
  logger.log('PrivateRoute判定ログ:', {
    hasToken: !!token,
    tokenLength: token?.length,
    hasUser: !!user,
    userName: user?.name,
    hasRefreshToken: !!refreshToken,
    refreshTokenLength: refreshToken?.length,
    isCheckingAuth,
    isLoading,
    redirectToLogin: !(token && (user || refreshToken))
  });

  // メモリストレージのチェックを含む拡張判定ロジック:
  // 1. Redux状態にトークンがあれば許可
  // 2. Redux状態にユーザー情報があれば許可
  // 3. tokenManagerからリフレッシュトークンがあれば許可（リロード時に重要）

      // 認証状態をチェック
      const hasToken = tokenManager.hasValidToken();
      const hasRefreshToken = tokenManager.hasRefreshToken();

      logger.log('PrivateRoute: 詳細認証状態チェック', {
        reduxToken: !!token,
        reduxUser: !!user,
        hasValidToken: hasToken,
        refreshTokenExists: hasRefreshToken,
        fromTokenManager: true
      });

      const authenticatedContent = embedded ? (
        <>{children}</>
      ) : (
        <TrialExpiredProvider>
          <TrialBanner />
          <FixedHeaderActions />
          {children}
          <React.Suspense fallback={null}><AdmanWidget /></React.Suspense>
        </TrialExpiredProvider>
      );

      if (token || user) {
        logger.log('PrivateRoute: Reduxストアに認証情報があるためアクセス許可');
        return authenticatedContent;
      } else if (hasToken) {
        // Reduxにトークンがなくても、tokenManagerに有効なトークンがあれば許可
        // これによりリロード時も認証状態を維持できる
        logger.log('PrivateRoute: 有効なアクセストークンが存在するためアクセス許可');
        return authenticatedContent;
      } else if (hasRefreshToken) {
        // アクセストークンがなくても、リフレッシュトークンがあれば許可
        logger.log('PrivateRoute: リフレッシュトークンが存在するためアクセス許可');
        return authenticatedContent;
      } else {
        logger.log('PrivateRoute: 有効な認証情報が見つからないためアクセス拒否');
        // ログイン後に元いた画面へ戻せるよう、遷移直前に現在地を控える
        saveReturnTo(location.pathname + location.search);
        // 広告着地時の twclid/utm_* を /login まで引き継ぐ（クエリ消失で X/GA4 計測が壊れるため）
        return <Navigate to={{ pathname: '/login', search: location.search }} replace />;
      }
};

export default PrivateRoute;
